// src/contexts/GenogramContext.js
import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { NodeType } from '../constants/nodeTypes';

const GenogramContext = createContext();

const ensureNodeDefaults = (person = {}) => {
  const type = person.type || NodeType.PERSON;
  const base = {
    ...person,
    type,
    notes: person.notes ?? '',
    notesRichText: typeof person.notesRichText === 'string' ? person.notesRichText : '',
    typeData: person.typeData && typeof person.typeData === 'object' && !Array.isArray(person.typeData)
      ? person.typeData
      : {},
    deceasedGentleTreatment: person.deceasedGentleTreatment ?? 'none'
  };

  if (type === NodeType.PERSON) {
    base.deceasedSymbol = base.deceasedSymbol ?? 'halo';
    if (Object.prototype.hasOwnProperty.call(person, 'networkNotes')) {
      base.networkNotes = person.networkNotes ?? '';
    } else if (person.networkMember && typeof person.notes === 'string' && person.notes.trim().length > 0) {
      base.networkNotes = person.notes;
    } else if (base.networkNotes === undefined) {
      base.networkNotes = '';
    }
  } else if (Object.prototype.hasOwnProperty.call(person, 'networkNotes')) {
    base.networkNotes = person.networkNotes ?? '';
  }

  return base;
};

const mergeNodeUpdates = (person, updates = {}) => {
  const hasTypeDataUpdate = Object.prototype.hasOwnProperty.call(updates, 'typeData');
  let nextTypeData = person.typeData || {};

  if (hasTypeDataUpdate) {
    const updateValue = updates.typeData;
    if (updateValue === null) {
      nextTypeData = {};
    } else if (updateValue && typeof updateValue === 'object' && !Array.isArray(updateValue)) {
      nextTypeData = { ...nextTypeData, ...updateValue };
    } else {
      nextTypeData = updateValue;
    }
  }

  const merged = {
    ...person,
    ...updates,
    typeData: nextTypeData
  };

  return ensureNodeDefaults(merged);
};

const initialState = {
  // Data
  people: [],
  relationships: [],
  households: [],
  textBoxes: [],
  customAttributes: [],
  nextPersonPosition: null,
  
  // Selection
  selectedPerson: null,
  selectedRelationship: null,
  selectedTextBox: null,
  selectedHousehold: null,
  
  // Connection mode
  isConnecting: false,
  connectingFrom: null,
  connectingFromMarriage: null,
  connectingType: 'marriage',
  connectionPreview: null,
  
  // Drawing mode
  isDrawingHousehold: false,
  currentHouseholdPoints: [],
  
  // UI State
  sidePanelOpen: false,
  quickAddOpen: false,
  contextMenu: null,
  deleteConfirmation: null,
  newPersonModalOpen: false,
  quickEditOpen: false,
  mobileMenuOpen: false,
  familyFinderOpen: false,
  childConnectionOptions: null,
  
  // Canvas State
  zoom: 1,
  pan: { x: 0, y: 0 },
  snapToGrid: true,
  highlightNetwork: false,
  focusedNodeId: null,
  
  // File State
  fileName: 'Untitled Genogram',
  isDirty: false,
  
  // History
  history: [],
  historyIndex: -1
};

function genogramReducer(state, action) {
  switch (action.type) {
    // People Actions
    case 'ADD_PERSON':
      const normalizedPerson = ensureNodeDefaults(action.payload);
      return {
        ...state,
        people: [...state.people, normalizedPerson],
        selectedPerson: normalizedPerson,
        selectedRelationship: null,
        selectedTextBox: null,
        selectedHousehold: null,
        sidePanelOpen: true,
        isDirty: true
      };

    case 'SET_NEXT_PERSON_POSITION':
      return {
        ...state,
        nextPersonPosition: action.payload
      };
    case 'SET_QUICK_EDIT_OPEN':
      return {
        ...state,
        quickEditOpen: action.payload
      };
      
    case 'SET_MOBILE_MENU_OPEN':
      return {
        ...state,
        mobileMenuOpen: action.payload
      };

    case 'SET_FAMILY_FINDER_OPEN':
      return {
        ...state,
        familyFinderOpen: action.payload
      };
      
    case 'SET_CHILD_CONNECTION_OPTIONS':
      return {
        ...state,
        childConnectionOptions: action.payload,
        isConnecting: false,
        connectingFrom: null,
        connectingFromMarriage: null,
        connectingType: 'marriage',
        connectionPreview: null
      };  

    case 'UPDATE_PERSON':
      return {
        ...state,
        people: state.people.map(p => 
          p.id === action.payload.id 
            ? mergeNodeUpdates(p, action.payload.updates)
            : p
        ),
        selectedPerson: state.selectedPerson?.id === action.payload.id
          ? mergeNodeUpdates(state.selectedPerson, action.payload.updates)
          : state.selectedPerson,
        isDirty: true
      };
      
    case 'DELETE_PERSON':
      const removedRelIds = state.relationships
        .filter(r => r.from === action.payload || r.to === action.payload)
        .map(r => r.id);
      
      // Check if the person being deleted is a child
      const childRelationship = state.relationships.find(r => 
        r.type === 'child' && r.to === action.payload
      );
      
      let updatedPeopleAfterDelete = state.people.filter(p => p.id !== action.payload);
      
      // If this person was a child, re-space their siblings
      if (childRelationship) {
        const parentRelationshipId = childRelationship.from;
        
        // Find the parent relationship
        const parentRelationship = state.relationships.find(r => r.id === parentRelationshipId);
        
        if (parentRelationship) {
          // Find the parents
          const parent1 = state.people.find(p => p.id === parentRelationship.from);
          const parent2 = state.people.find(p => p.id === parentRelationship.to);
          
          // Calculate center position between parents
          let centerX;
          if (parent1 && parent2) {
            centerX = (parent1.x + parent2.x) / 2;
          } else if (parent1) {
            centerX = parent1.x;
          } else if (parent2) {
            centerX = parent2.x;
          }
          
          if (centerX !== undefined) {
            // Find all remaining siblings of this parent relationship
            const remainingSiblingRelationships = state.relationships.filter(r =>
              r.type === 'child' && 
              r.from === parentRelationshipId && 
              r.to !== action.payload
            );
            
            const remainingSiblings = remainingSiblingRelationships.map(rel =>
              updatedPeopleAfterDelete.find(p => p.id === rel.to)
            ).filter(Boolean);
            
            if (remainingSiblings.length > 0) {
              // Sort siblings by current X position
              const sortedSiblings = [...remainingSiblings].sort((a, b) => a.x - b.x);
              
              // Calculate new positions with proper spacing
              const SPACING = 100;
              const totalWidth = (sortedSiblings.length - 1) * SPACING;
              const startX = centerX - (totalWidth / 2);
              
              console.log(`Re-spacing ${sortedSiblings.length} remaining siblings after deletion`);
              
              // Update positions of remaining siblings
              sortedSiblings.forEach((sibling, index) => {
                const newX = startX + (index * SPACING);
                const personIndex = updatedPeopleAfterDelete.findIndex(p => p.id === sibling.id);
                if (personIndex !== -1) {
                  console.log(`Moving sibling "${sibling.name}" to X=${newX}`);
                  updatedPeopleAfterDelete[personIndex] = {
                    ...updatedPeopleAfterDelete[personIndex],
                    x: newX
                  };
                }
              });
            }
          }
        }
      }
      
      // Filter relationships
      const updatedRelationships = state.relationships.filter(r => {
        if (r.from === action.payload || r.to === action.payload) return false;
        if (r.type === 'child' && removedRelIds.includes(r.from)) return false;
        return true;
      });
      
      return {
        ...state,
        people: updatedPeopleAfterDelete,
        relationships: updatedRelationships,
        selectedPerson: null,
        selectedRelationship: null,
        focusedNodeId: state.focusedNodeId === action.payload ? null : state.focusedNodeId,
        isDirty: true
      };
      
    // Relationship Actions
    case 'ADD_RELATIONSHIP':
      return {
        ...state,
        relationships: [...state.relationships, action.payload],
        isDirty: true
      };
      
    case 'UPDATE_RELATIONSHIP':
      console.log('=== UPDATE_RELATIONSHIP - PRESERVING CHILDREN ===');
      console.log('Updating relationship:', action.payload.id);
      console.log('Updates:', action.payload.updates);
      
      // Check if this relationship has children
      const childRelationships = state.relationships.filter(r => 
        r.type === 'child' && r.from === action.payload.id
      );
      
      if (childRelationships.length > 0) {
        console.log(`⚠️  PRESERVING ${childRelationships.length} child relationships for updated relationship`);
        console.log('Child relationships:', childRelationships.map(r => r.id));
      }
      
      // Update the relationship while preserving all existing child relationships
      return {
        ...state,
        relationships: state.relationships.map(r => 
          r.id === action.payload.id 
            ? { ...r, ...action.payload.updates } 
            : r
        ),
        selectedRelationship: state.selectedRelationship?.id === action.payload.id
          ? { ...state.selectedRelationship, ...action.payload.updates }
          : state.selectedRelationship,
        isDirty: true
      };
      
    case 'DELETE_RELATIONSHIP':
      console.log('=== DELETE_RELATIONSHIP - CHECKING CHILDREN ===');
      console.log('Deleting relationship:', action.payload);
      
      // Check if this relationship has children
      const childrenToDelete = state.relationships.filter(r => 
        r.type === 'child' && r.from === action.payload
      );
      
      if (childrenToDelete.length > 0) {
        console.log(`⚠️  WARNING: Deleting relationship will also remove ${childrenToDelete.length} child relationships`);
        console.log('Child relationships being deleted:', childrenToDelete.map(r => r.id));
        
        // Find the child people who will lose their parent relationship
        const childPeople = childrenToDelete.map(childRel => {
          const child = state.people.find(p => p.id === childRel.to);
          return child ? child.name : 'Unknown';
        });
        console.log('Children affected:', childPeople);
      }
      
      return {
        ...state,
        relationships: state.relationships.filter(r => {
          if (r.id === action.payload) return false;
          if (r.type === 'child' && r.from === action.payload) return false;
          return true;
        }),
        selectedRelationship: null,
        isDirty: true
      };
      
    // Household Actions
    case 'ADD_HOUSEHOLD':
      return {
        ...state,
        households: [...state.households, action.payload],
        selectedHousehold: action.payload,
        isDrawingHousehold: false,
        currentHouseholdPoints: [],
        isDirty: true
      };
      
    case 'UPDATE_HOUSEHOLD':
      return {
        ...state,
        households: state.households.map(h => 
          h.id === action.payload.id 
            ? { ...h, ...action.payload.updates } 
            : h
        ),
        selectedHousehold: state.selectedHousehold?.id === action.payload.id
          ? { ...state.selectedHousehold, ...action.payload.updates }
          : state.selectedHousehold,
        isDirty: true
      };
      
    case 'DELETE_HOUSEHOLD':
      return {
        ...state,
        households: state.households.filter(h => h.id !== action.payload),
        selectedHousehold: null,
        isDirty: true
      };
      
    case 'ADD_POINT_TO_HOUSEHOLD':
      return {
        ...state,
        households: state.households.map(h => {
          if (h.id === action.payload.householdId) {
            const newPoints = [...h.points];
            // Insert the new point at the specified index, or at the end if no index
            if (action.payload.index !== undefined) {
              newPoints.splice(action.payload.index + 1, 0, action.payload.point);
            } else {
              newPoints.push(action.payload.point);
            }
            return { ...h, points: newPoints };
          }
          return h;
        }),
        isDirty: true
      };  

    // Text Box Actions
    case 'ADD_TEXTBOX':
      return {
        ...state,
        textBoxes: [...state.textBoxes, action.payload],
        selectedTextBox: action.payload,
        selectedPerson: null,
        selectedRelationship: null,
        selectedHousehold: null,
        sidePanelOpen: true,
        isDirty: true
      };
      
    case 'UPDATE_TEXTBOX':
      return {
        ...state,
        textBoxes: state.textBoxes.map(t => 
          t.id === action.payload.id 
            ? { ...t, ...action.payload.updates } 
            : t
        ),
        selectedTextBox: state.selectedTextBox?.id === action.payload.id
          ? { ...state.selectedTextBox, ...action.payload.updates }
          : state.selectedTextBox,
        isDirty: true
      };
      
    case 'DELETE_TEXTBOX':
      return {
        ...state,
        textBoxes: state.textBoxes.filter(t => t.id !== action.payload),
        selectedTextBox: null,
        isDirty: true
      };

    // Custom Attributes Actions
    case 'SET_CUSTOM_ATTRIBUTES':
      return {
        ...state,
        customAttributes: action.payload,
        isDirty: true
      };

    // Selection Actions
    case 'SELECT_PERSON':
      const person = action.payload?.person || action.payload;
      const shouldOpenPanel = action.payload?.openPanel !== false && person !== null;
      const nextFocusedNodeId = person && state.selectedPerson?.id === person?.id
        ? state.focusedNodeId
        : null;
      return {
        ...state,
        selectedPerson: person,
        selectedRelationship: null,
        selectedTextBox: null,
        selectedHousehold: null,
        sidePanelOpen: shouldOpenPanel,
        focusedNodeId: nextFocusedNodeId
      };

    case 'SET_NEW_PERSON_MODAL':
      return {
       ...state,
       newPersonModalOpen: action.payload
      };  

    case 'SELECT_RELATIONSHIP':
      const relationship = action.payload?.relationship || action.payload;
      const shouldOpenRelationshipPanel = action.payload?.openPanel !== false && relationship !== null;
      return {
        ...state,
        selectedRelationship: relationship,
        selectedPerson: null,
        selectedTextBox: null,
        selectedHousehold: null,
        sidePanelOpen: shouldOpenRelationshipPanel,
        focusedNodeId: null
      };
      
    case 'SELECT_TEXTBOX':
      const textBox = action.payload?.textBox || action.payload;
      const shouldOpenTextBoxPanel = action.payload?.openPanel !== false && textBox !== null;
      return {
        ...state,
        selectedTextBox: textBox,
        selectedPerson: null,
        selectedRelationship: null,
        selectedHousehold: null,
        sidePanelOpen: shouldOpenTextBoxPanel,
        focusedNodeId: null
      };
      
    case 'SELECT_HOUSEHOLD':
      return {
        ...state,
        selectedHousehold: action.payload,
        selectedPerson: null,
        selectedRelationship: null,
        selectedTextBox: null,
        sidePanelOpen: action.payload !== null,
        focusedNodeId: null
      };
      
    case 'CLEAR_SELECTION':
      return {
        ...state,
        selectedPerson: null,
        selectedRelationship: null,
        selectedTextBox: null,
        selectedHousehold: null,
        contextMenu: null,
        sidePanelOpen: false,
        mobileMenuOpen: false,
        focusedNodeId: null
      };
      
    // Connection Mode
    case 'START_CONNECTION':
      console.log('=== START_CONNECTION REDUCER DEBUG ===');
      console.log('Payload:', action.payload);
      console.log('Setting isConnecting to true');
      console.log('connectingFrom:', action.payload.personId);
      console.log('connectingFromMarriage:', action.payload.marriageId);
      console.log('connectingType:', action.payload.type);
      
      return {
        ...state,
        isConnecting: true,
        connectingFrom: action.payload.personId,
        connectingFromMarriage: action.payload.marriageId,
        connectingType: action.payload.type,
        connectionPreview: null
      };
      
    case 'CANCEL_CONNECTION':
      console.log('=== CANCEL_CONNECTION REDUCER DEBUG ===');
      console.log('Canceling connection mode');
      console.log('Previous state:', {
        isConnecting: state.isConnecting,
        connectingFrom: state.connectingFrom,
        connectingType: state.connectingType
      });
      
      return {
        ...state,
        isConnecting: false,
        connectingFrom: null,
        connectingFromMarriage: null,
        connectingType: 'marriage',
        connectionPreview: null
      };
      
    // Household Drawing
    case 'START_DRAWING_HOUSEHOLD':
      return {
        ...state,
        isDrawingHousehold: true,
        currentHouseholdPoints: []
      };
      
    case 'ADD_HOUSEHOLD_POINT':
      return {
        ...state,
        currentHouseholdPoints: [...state.currentHouseholdPoints, action.payload]
      };
      
    case 'FINISH_HOUSEHOLD':
      if (state.currentHouseholdPoints.length >= 3) {
        // Generate default household name
        const existingHouseholdNumbers = state.households
          .map(h => h.label || h.name || '')
          .filter(name => /^Household \d+$/.test(name))
          .map(name => parseInt(name.replace('Household ', '')))
          .filter(num => !isNaN(num))
          .sort((a, b) => a - b);
        
        let nextNumber = 1;
        for (const num of existingHouseholdNumbers) {
          if (num === nextNumber) {
            nextNumber++;
          } else {
            break;
          }
        }
        
        const newHousehold = {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          points: [...state.currentHouseholdPoints],
          label: `Household ${nextNumber}`,
          name: `Household ${nextNumber}`, // Also set name for compatibility
          color: '#6366f1'
        };
        
        return {
          ...state,
          households: [...state.households, newHousehold],
          selectedHousehold: newHousehold,
          isDrawingHousehold: false,
          currentHouseholdPoints: [],
          sidePanelOpen: true,
          isDirty: true
        };
      }
      return state;
      
    case 'CANCEL_DRAWING_HOUSEHOLD':
      return {
        ...state,
        isDrawingHousehold: false,
        currentHouseholdPoints: []
      };
      
    // UI Actions
    case 'SET_CONTEXT_MENU':
      return {
        ...state,
        contextMenu: action.payload
      };
      
    case 'SET_DELETE_CONFIRMATION':
      return {
        ...state,
        deleteConfirmation: action.payload
      };
      
    case 'TOGGLE_SIDE_PANEL':
      return {
        ...state,
        sidePanelOpen: !state.sidePanelOpen
      };

    case 'SET_SIDE_PANEL_OPEN':
      return {
        ...state,
        sidePanelOpen: Boolean(action.payload)
      };
      
    case 'SET_QUICK_ADD_OPEN':
      return {
        ...state,
        quickAddOpen: action.payload
      };
      
    // Canvas Actions
    case 'SET_ZOOM':
      return {
        ...state,
        zoom: action.payload
      };
      
    case 'SET_PAN':
      return {
        ...state,
        pan: action.payload
      };

    case 'SET_CONNECTION_PREVIEW':
      return {
        ...state,
        connectionPreview: action.payload
      };
      
    case 'TOGGLE_SNAP_TO_GRID':
      return {
        ...state,
        snapToGrid: !state.snapToGrid
      };
      
    case 'TOGGLE_HIGHLIGHT_NETWORK':
      const nextHighlightNetwork = !state.highlightNetwork;
      return {
        ...state,
        highlightNetwork: nextHighlightNetwork,
        focusedNodeId: nextHighlightNetwork ? null : state.focusedNodeId
      };

    case 'SET_FOCUSED_NODE':
      return {
        ...state,
        focusedNodeId: action.payload
      };
      
    // File Actions
    case 'SET_FILE_NAME':
      return {
        ...state,
        fileName: action.payload
      };
      
    case 'SET_DIRTY':
      return {
        ...state,
        isDirty: action.payload
      };
      
    case 'LOAD_DATA':
      return {
        ...state,
        people: (action.payload.people || []).map(person => ensureNodeDefaults(person)),
        relationships: action.payload.relationships || [],
        households: action.payload.households || [],
        textBoxes: action.payload.textBoxes || [],
        customAttributes: action.payload.customAttributes || [],
        fileName: action.payload.fileName || 'Untitled Genogram',
        isDirty: false,
        selectedPerson: null,
        selectedRelationship: null,
        selectedTextBox: null,
        selectedHousehold: null,
        focusedNodeId: null,
        // Reset zoom and pan when loading new data to ensure fitToCanvas works properly
        zoom: 1,
        pan: { x: 0, y: 0 }
      };
      
    case 'NEW_GENOGRAM':
      return {
        ...initialState,
        zoom: state.zoom,
        pan: state.pan,
        snapToGrid: state.snapToGrid
      };
      
    // History Actions
    case 'SAVE_TO_HISTORY':
      const newHistory = state.history.slice(0, state.historyIndex + 1);
      newHistory.push(action.payload);
      if (newHistory.length > 50) newHistory.shift();
      
      return {
        ...state,
        history: newHistory,
        historyIndex: Math.min(state.historyIndex + 1, 49)
      };
      
    case 'UNDO':
      if (state.historyIndex > 0) {
        const prevState = state.history[state.historyIndex - 1];
        return {
          ...state,
          people: prevState.people,
          relationships: prevState.relationships,
          households: prevState.households || [],
          textBoxes: prevState.textBoxes || [],
          historyIndex: state.historyIndex - 1,
          selectedPerson: null,
          selectedRelationship: null,
          selectedTextBox: null,
          selectedHousehold: null
        };
      }
      return state;
      
    case 'REDO':
      if (state.historyIndex < state.history.length - 1) {
        const nextState = state.history[state.historyIndex + 1];
        return {
          ...state,
          people: nextState.people,
          relationships: nextState.relationships,
          households: nextState.households || [],
          textBoxes: nextState.textBoxes || [],
          historyIndex: state.historyIndex + 1,
          selectedPerson: null,
          selectedRelationship: null,
          selectedTextBox: null,
          selectedHousehold: null
        };
      }
      return state;

    // Handle batch updates for custom actions
    case 'BATCH_UPDATE':
      console.log('BATCH_UPDATE - payload:', action.payload);
      console.log('BATCH_UPDATE - newPersonModalOpen:', action.payload.newPersonModalOpen);
      return {
        ...state,
        ...action.payload,
        isDirty: true
      };

    default:
      return state;
  }
} // <-- Close genogramReducer function

export function GenogramProvider({ children }) {
  const [state, dispatch] = useReducer(genogramReducer, initialState);
  const lastBroadcastRef = useRef('');
  
  // Grid snapping utility
  const GRID_SIZE = 20;
  const snapToGridFunc = React.useCallback((value) => {
    if (!state.snapToGrid) return value;
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
  }, [state.snapToGrid]);
  
  // Add user-driven override from App toggle
const [userInteractionOverride, setUserInteractionOverride] = React.useState(() => {
  const attr = document.body.getAttribute('data-interaction') || document.documentElement.getAttribute('data-interaction');
  return attr === 'touch' || attr === 'desktop' ? attr : null;
});

React.useEffect(() => {
  const handler = (e) => {
    const mode = e?.detail?.mode || document.body.getAttribute('data-interaction') || document.documentElement.getAttribute('data-interaction');
    setUserInteractionOverride(mode === 'touch' || mode === 'desktop' ? mode : null);
  };
  window.addEventListener('interaction-mode-changed', handler);
  return () => window.removeEventListener('interaction-mode-changed', handler);
}, []);

// Base detection used when there is no user override
const baseIsMobile = React.useMemo(() => {
	try {
		if (typeof window !== 'undefined') {
			if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return true;
			const touchCapable = 'ontouchstart' in window || (navigator && navigator.maxTouchPoints > 0);
			const smallScreen = window.innerWidth <= 1024;
			return touchCapable || smallScreen;
		}
		return false;
	} catch {
		return false;
	}
}, []);

const baseIsTouch = baseIsMobile;

// Effective flags honor user override first, then fall back to base detection
const effectiveIsMobile = React.useMemo(() => {
	if (userInteractionOverride === 'touch') return true;
	if (userInteractionOverride === 'desktop') return false;
	return baseIsMobile;
}, [baseIsMobile, userInteractionOverride]);

const effectiveIsTouch = React.useMemo(() => {
	if (userInteractionOverride === 'touch') return true;
	if (userInteractionOverride === 'desktop') return false;
	return baseIsTouch;
}, [baseIsTouch, userInteractionOverride]);

  // Helper functions that wrap dispatch
  const actions = React.useMemo(() => {
    // Helper functions (formerly useCallback, now just functions using latest state)
    const addPerson = (person) => {
      const normalized = ensureNodeDefaults(person);
      dispatch({ type: 'ADD_PERSON', payload: normalized });
    };
    const updatePerson = (id, updates) => dispatch({ type: 'UPDATE_PERSON', payload: { id, updates } });
    const deletePerson = (id) => dispatch({ type: 'DELETE_PERSON', payload: id });
    const setNextPersonPosition = (position) => dispatch({ type: 'SET_NEXT_PERSON_POSITION', payload: position });
    const setNewPersonModal = (open) => dispatch({ type: 'SET_NEW_PERSON_MODAL', payload: open });

    const addRelationship = (relationship) => dispatch({ type: 'ADD_RELATIONSHIP', payload: relationship });
    const updateRelationship = (id, updates) => dispatch({ type: 'UPDATE_RELATIONSHIP', payload: { id, updates } });
    const deleteRelationship = (id) => dispatch({ type: 'DELETE_RELATIONSHIP', payload: id });

    const addHousehold = (household) => dispatch({ type: 'ADD_HOUSEHOLD', payload: household });
    const updateHousehold = (id, updates) => dispatch({ type: 'UPDATE_HOUSEHOLD', payload: { id, updates } });
    const deleteHousehold = (id) => dispatch({ type: 'DELETE_HOUSEHOLD', payload: id });

    const addTextBox = (textBox) => dispatch({ type: 'ADD_TEXTBOX', payload: textBox });
    const updateTextBox = (id, updates) => dispatch({ type: 'UPDATE_TEXTBOX', payload: { id, updates } });
    const deleteTextBox = (id) => dispatch({ type: 'DELETE_TEXTBOX', payload: id });

    const setCustomAttributes = (attributes) => dispatch({ type: 'SET_CUSTOM_ATTRIBUTES', payload: attributes });

    const selectPerson = (person, forceEdit = false) => {
      // Handle new format: { person, openPanel } or legacy format: just person
      const personObj = person?.person || person;
      const openPanel = person?.openPanel;
      
      if (state.highlightNetwork && !forceEdit) {
        // Network toggle mode - get current person from state to avoid stale data
        const currentPerson = state.people.find(p => p.id === personObj.id);
        if (currentPerson) {
          dispatch({ type: 'UPDATE_PERSON', payload: { id: personObj.id, updates: { networkMember: !currentPerson.networkMember } } });
        }
      } else {
        // Pass the openPanel parameter if specified
        if (openPanel !== undefined) {
          dispatch({ type: 'SELECT_PERSON', payload: { person: personObj, openPanel } });
        } else {
          dispatch({ type: 'SELECT_PERSON', payload: personObj });
        }
      }
    };
    const selectRelationship = (relationship, openPanel = true) => {
      // Handle new format: { relationship, openPanel } or legacy format: just relationship
      const relationshipObj = relationship?.relationship || relationship;
      const shouldOpenPanel = relationship?.openPanel !== undefined ? relationship.openPanel : openPanel;
      
      if (shouldOpenPanel !== undefined) {
        dispatch({ type: 'SELECT_RELATIONSHIP', payload: { relationship: relationshipObj, openPanel: shouldOpenPanel } });
      } else {
        dispatch({ type: 'SELECT_RELATIONSHIP', payload: relationshipObj });
      }
    };
    const selectTextBox = (textBox, openPanel = true) => {
      // Handle new format: { textBox, openPanel } or legacy format: just textBox
      const textBoxObj = textBox?.textBox || textBox;
      const shouldOpenPanel = textBox?.openPanel !== undefined ? textBox.openPanel : openPanel;
      
      if (shouldOpenPanel !== undefined) {
        dispatch({ type: 'SELECT_TEXTBOX', payload: { textBox: textBoxObj, openPanel: shouldOpenPanel } });
      } else {
        dispatch({ type: 'SELECT_TEXTBOX', payload: textBoxObj });
      }
    };
    const selectHousehold = (household) => dispatch({ type: 'SELECT_HOUSEHOLD', payload: household });
    const clearSelection = () => dispatch({ type: 'CLEAR_SELECTION' });
    const setFocusedNode = (nodeId) => dispatch({ type: 'SET_FOCUSED_NODE', payload: nodeId });
    const exitFocusMode = () => dispatch({ type: 'SET_FOCUSED_NODE', payload: null });

    const startConnection = (personId, marriageId, type) =>
      dispatch({ type: 'START_CONNECTION', payload: { personId, marriageId, type } });
    const cancelConnection = () => dispatch({ type: 'CANCEL_CONNECTION' });

    const startDrawingHousehold = () => dispatch({ type: 'START_DRAWING_HOUSEHOLD' });
    const addHouseholdPoint = (point) => dispatch({ type: 'ADD_HOUSEHOLD_POINT', payload: point });
    const finishHousehold = () => dispatch({ type: 'FINISH_HOUSEHOLD' });
    const cancelDrawingHousehold = () => dispatch({ type: 'CANCEL_DRAWING_HOUSEHOLD' });

    const setContextMenu = (menu) => dispatch({ type: 'SET_CONTEXT_MENU', payload: menu });
    const setDeleteConfirmation = (confirmation) => dispatch({ type: 'SET_DELETE_CONFIRMATION', payload: confirmation });
    const toggleSidePanel = () => dispatch({ type: 'TOGGLE_SIDE_PANEL' });
  const setSidePanelOpen = (open) => dispatch({ type: 'SET_SIDE_PANEL_OPEN', payload: open });
    const setQuickAddOpen = (open) => dispatch({ type: 'SET_QUICK_ADD_OPEN', payload: open });
    const setQuickEditOpen = (open) => dispatch({ type: 'SET_QUICK_EDIT_OPEN', payload: open });
    const setMobileMenuOpen = (open) => dispatch({ type: 'SET_MOBILE_MENU_OPEN', payload: open });
    const setFamilyFinderOpen = (open) => dispatch({ type: 'SET_FAMILY_FINDER_OPEN', payload: open });
    const handlePersonClick = (person, options = {}) => {
      if (!person) {
        return;
      }

      const nodeType = person.type || NodeType.PERSON;
      const { openPanel } = options;

      if (state.highlightNetwork && nodeType === NodeType.PERSON) {
        const currentPerson = state.people.find((p) => p.id === person.id);
        if (currentPerson) {
          dispatch({
            type: 'UPDATE_PERSON',
            payload: {
              id: person.id,
              updates: { networkMember: !currentPerson.networkMember }
            }
          });
        }
        return;
      }

      if (state.selectedPerson?.id === person.id) {
        if (!state.sidePanelOpen) {
          dispatch({ type: 'SET_SIDE_PANEL_OPEN', payload: true });
        }

        if (state.focusedNodeId === person.id) {
          dispatch({ type: 'SET_FOCUSED_NODE', payload: null });
        } else {
          dispatch({ type: 'SET_FOCUSED_NODE', payload: person.id });
        }
        return;
      }

      if (openPanel === undefined) {
        dispatch({ type: 'SELECT_PERSON', payload: person });
      } else {
        dispatch({ type: 'SELECT_PERSON', payload: { person, openPanel } });
      }
    };
    

    const setZoom = (zoom) => dispatch({ type: 'SET_ZOOM', payload: zoom });
    const setPan = (pan) => dispatch({ type: 'SET_PAN', payload: pan });
    const setConnectionPreview = (preview) =>
      dispatch({ type: 'SET_CONNECTION_PREVIEW', payload: preview });
    const toggleSnapToGrid = () => dispatch({ type: 'TOGGLE_SNAP_TO_GRID' });
    const toggleHighlightNetwork = () => dispatch({ type: 'TOGGLE_HIGHLIGHT_NETWORK' });

    const setFileName = (name) => dispatch({ type: 'SET_FILE_NAME', payload: name });
    const setDirty = (dirty) => dispatch({ type: 'SET_DIRTY', payload: dirty });
    const loadData = (data) => dispatch({ type: 'LOAD_DATA', payload: data });
    const newGenogram = () => dispatch({ type: 'NEW_GENOGRAM' });

    const saveToHistory = (stateToSave) => dispatch({ type: 'SAVE_TO_HISTORY', payload: stateToSave });
    const undo = () => dispatch({ type: 'UNDO' });
    const redo = () => dispatch({ type: 'REDO' });

    // Create a relationship between two existing people
    const createRelationship = (fromId, toId, type) => {
      const colors = ['#ec4899', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];
      const colorIndex = state.relationships.length % colors.length;
      const newRelationship = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        from: fromId,
        to: toId,
        type,
        color: colors[colorIndex],
        lineStyle: 'default',
        startDate: '',
        endDate: '',
        isActive: !['divorce', 'separation', 'nullity'].includes(type),
        abbr: state.REL_ABBREVIATIONS?.[type] || '',
        notes: '',
        bubblePosition: 0.5
      };
      
      // Add the relationship, cancel connection mode, and auto-select for editing
      dispatch({ type: 'ADD_RELATIONSHIP', payload: newRelationship });
      dispatch({ type: 'CANCEL_CONNECTION' }); // End connection mode immediately
      
      // Use timeout to ensure the relationship is added before selecting it
      setTimeout(() => {
        dispatch({ type: 'SELECT_RELATIONSHIP', payload: newRelationship });
      }, 100);
    };

    // Link an existing person as a child of a relationship
    const createChildRelationship = (relationshipId, childId) => {
      const rel = state.relationships.find(r => r.id === relationshipId);
      const childRelationship = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_childrel',
        from: relationshipId,
        to: childId,
        type: 'child',
        color: rel?.color || '#3b82f6',
        lineStyle: 'default',
        startDate: '',
        endDate: '',
        isActive: true,
        abbr: state.REL_ABBREVIATIONS?.['child'] || '',
        notes: '',
        bubblePosition: 0.5
      };
      dispatch({ type: 'ADD_RELATIONSHIP', payload: childRelationship });
    };

    const createChildWithUnknownParent = (person) => {
     console.log('createChildWithUnknownParent called for:', person.name);

      // Create unknown parent
      const unknownParent = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        name: 'Unknown',
        type: NodeType.PERSON,
        gender: 'unknown',
        x: snapToGridFunc(person.x + 150),
        y: snapToGridFunc(person.y),
  age: '',
  birthDate: '',
  deceasedSymbol: 'halo',
  deceasedGentleTreatment: 'none',
        isDeceased: false,
        specialStatus: null,
        networkMember: false
      };

      // Create partnership
      const partnership = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_rel',
        from: person.id,
        to: unknownParent.id,
        type: 'partner',
        color: '#9ca3af',
        lineStyle: 'dashed',
        isActive: false,
        notes: 'Unknown co-parent',
        bubblePosition: 0.5,
        startDate: '',
        endDate: '',
        abbr: ''
      };

      // Create the child
      const child = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_child',
        name: 'New Child',
        type: NodeType.PERSON,
        gender: 'unknown',
        x: snapToGridFunc(person.x),
        y: snapToGridFunc(person.y + 150),
  age: '',
  birthDate: '',
  deceasedSymbol: 'halo',
  deceasedGentleTreatment: 'none',
        isDeceased: false,
        specialStatus: null,
        networkMember: false
      };

      // Create child relationship
      const childRelationship = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_childrel',
        from: partnership.id,
        to: child.id,
        type: 'child',
        color: partnership.color,
        lineStyle: 'default',
        isActive: true
      };

      console.log('Creating:', { unknownParent, partnership, child, childRelationship });

      // Update state with all items at once
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          people: [...state.people, unknownParent, child],
          relationships: [...state.relationships, partnership, childRelationship],
          selectedPerson: child,
          selectedRelationship: null,
          sidePanelOpen: true,
          connectionPreview: null
        }
      });
    };

    const createSingleParentAdoption = (person) => {
      console.log('createSingleParentAdoption called for:', person.name);

      // Create adoption relationship (self-referential)
      const adoptionRelationship = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_adopt',
        from: person.id,
        to: person.id,
        type: 'adoption',
        color: '#06b6d4',
        lineStyle: 'dotted',
        isActive: true,
        notes: 'Single parent adoption',
        bubblePosition: 0.5,
        abbr: 'A'
      };

      // Create adopted child
      const child = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_child',
        name: 'Adopted Child',
        type: NodeType.PERSON,
        gender: 'unknown',
        x: snapToGridFunc(person.x),
        y: snapToGridFunc(person.y + 150),
        age: '',
        birthDate: '',
    deceasedSymbol: 'halo',
    deceasedGentleTreatment: 'none',
        isDeceased: false,
        specialStatus: 'adopted',
        networkMember: false
      };

      // Create child relationship
      const childRelationship = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_childrel',
        from: adoptionRelationship.id,
        to: child.id,
        type: 'child',
        color: adoptionRelationship.color,
        lineStyle: 'dashed',
        isActive: true
      };

      console.log('Creating:', { adoptionRelationship, child, childRelationship });

      // Update state with all items
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          people: [...state.people, child],
          relationships: [...state.relationships, adoptionRelationship, childRelationship],
          selectedPerson: child,
          selectedRelationship: null,
          sidePanelOpen: true,
          connectionPreview: null
        }
      });
    };

    const connectExistingPersonAsChild = (parentPerson, childPerson) => {
      console.log('connectExistingPersonAsChild called:', {
        parent: parentPerson.name,
        child: childPerson.name
      });

      // Find existing partner relationships for the parent
      const PARENT_RELATIONSHIP_TYPES = [
        // Romantic relationships
        'marriage', 'partner', 'cohabitation', 'engagement', 'dating', 'love-affair', 'secret-affair', 'single-encounter',
        // Ended relationships (they can still have children from when they were together)
        'divorce', 'separation', 'nullity', 'widowed',
        // Complex dynamics (can still have children)
        'complicated', 'on-off', 'toxic', 'dependency', 'codependent',
        // Special family relationships
        'adoption', 'step-relationship',
        // Emotional relationships that might have children
        'close', 'love', 'best-friends',
        // Social services relationships
        'caregiver', 'supportive'
      ];
      
      const partnerRelationships = state.relationships.filter(r => 
        (r.from === parentPerson.id || r.to === parentPerson.id) && 
        PARENT_RELATIONSHIP_TYPES.includes(r.type)
      );

      console.log('Found', partnerRelationships.length, 'existing partner relationships');

      if (partnerRelationships.length === 0) {
        // No partners - show child connection options
        console.log('No partners - showing child connection options');
        dispatch({
          type: 'SET_CHILD_CONNECTION_OPTIONS',
          payload: {
            isOpen: true,
            parentPerson: parentPerson,
            childPerson: childPerson,
            availablePartners: []
          }
        });
      } else if (partnerRelationships.length === 1) {
        // One partner - directly connect child to that relationship
        console.log('One partner - connecting directly');
        const partnership = partnerRelationships[0];
        connectChildToExistingRelationship(partnership.id, childPerson);
      } else {
        // Multiple partners - show selection options
        console.log('Multiple partners - showing selection options');
        dispatch({
          type: 'SET_CHILD_CONNECTION_OPTIONS',
          payload: {
            isOpen: true,
            parentPerson: parentPerson,
            childPerson: childPerson,
            availablePartners: partnerRelationships
          }
        });
      }
    };

    const connectChildToExistingRelationship = (relationshipId, childPerson) => {
      console.log('connectChildToExistingRelationship called:', {
        relationshipId,
        childName: childPerson.name
      });

      // Create child relationship from existing partnership to existing child
      const relationship = state.relationships.find(r => r.id === relationshipId);
      if (!relationship) {
        console.error('Relationship not found:', relationshipId);
        return;
      }

      const childRelationship = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_childrel',
        from: relationshipId,
        to: childPerson.id,
        type: 'child',
        color: relationship.color || '#3b82f6',
        lineStyle: 'default',
        isActive: true
      };

      console.log('Creating child relationship:', childRelationship);

      // Add the child relationship and clear connection state
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          people: state.people,
          relationships: [...state.relationships, childRelationship],
          selectedPerson: childPerson,
          selectedRelationship: null,
          isConnecting: false,
          connectingFrom: null,
          connectingFromMarriage: null,
          connectingType: 'marriage',
          connectionPreview: null,
          sidePanelOpen: false
        }
      });
    };

    const handleChildConnectionOption = (optionType, parentPerson, childPerson, relationship = null) => {
      console.log('handleChildConnectionOption called:', {
        optionType,
        parentName: parentPerson.name,
        childName: childPerson.name,
        relationship: relationship?.id
      });

      switch (optionType) {
        case 'unknown':
          // Create unknown co-parent and connect child
          createChildConnectionWithUnknownParent(parentPerson, childPerson);
          break;
        case 'newPartner':
          // Create new partner and connect child
          createChildConnectionWithNewPartner(parentPerson, childPerson);
          break;
        case 'singleAdoption':
          // Create single parent adoption and connect child
          createChildConnectionWithSingleAdoption(parentPerson, childPerson);
          break;
        case 'selectPartner':
          // Connect child to existing partner relationship
          connectChildToExistingRelationship(relationship.id, childPerson);
          break;
        case 'cancel':
          // Just close the modal without doing anything
          console.log('Child connection cancelled');
          break;
        default:
          break;
      }
      
      // Close the child connection options modal
      dispatch({
        type: 'SET_CHILD_CONNECTION_OPTIONS',
        payload: null
      });
    };

    const createChildConnectionWithUnknownParent = (parentPerson, childPerson) => {
      console.log('createChildConnectionWithUnknownParent called');

      // Create unknown parent
      const unknownParent = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        name: 'Unknown',
        type: NodeType.PERSON,
        gender: 'unknown',
        x: snapToGridFunc(parentPerson.x + 150),
        y: snapToGridFunc(parentPerson.y),
        age: '',
        birthDate: '',
        isDeceased: false,
        specialStatus: null,
        networkMember: false
      };

      // Create partnership between parent and unknown parent
      const partnership = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_rel',
        from: parentPerson.id,
        to: unknownParent.id,
        type: 'partner',
        color: '#9ca3af',
        lineStyle: 'dashed',
        isActive: false,
        notes: 'Unknown co-parent',
        bubblePosition: 0.5,
        startDate: '',
        endDate: '',
        abbr: ''
      };

      // Create child relationship from partnership to existing child
      const childRelationship = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_childrel',
        from: partnership.id,
        to: childPerson.id,
        type: 'child',
        color: partnership.color,
        lineStyle: 'default',
        isActive: true
      };

      // Update state with all items at once
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          people: [...state.people, unknownParent],
          relationships: [...state.relationships, partnership, childRelationship],
          selectedPerson: childPerson,
          selectedRelationship: null,
          isConnecting: false,
          connectingFrom: null,
          connectingFromMarriage: null,
          connectingType: 'marriage',
          connectionPreview: null,
          sidePanelOpen: false
        }
      });
    };

    const createChildConnectionWithNewPartner = (parentPerson, childPerson) => {
      console.log('createChildConnectionWithNewPartner called');

      // Create new partner
      const defaultGender = parentPerson.gender === 'male' ? 'female' : 
                           parentPerson.gender === 'female' ? 'male' : 
                           'female';

      const newPartner = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        name: `Person ${state.people.length + 1}`,
        type: NodeType.PERSON,
        gender: defaultGender,
        age: '',
        birthDate: '',
        deathDate: '',
    deceasedSymbol: 'halo',
    deceasedGentleTreatment: 'none',
        isDeceased: false,
        x: snapToGridFunc(parentPerson.x + 120),
        y: snapToGridFunc(parentPerson.y),
        generation: parentPerson.generation || 0,
        specialStatus: null,
        sexualOrientation: 'not-specified',
        networkMember: false,
        role: '',
        notes: ''
      };

      // Create marriage relationship
      const colors = ['#ec4899', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];
      const colorIndex = state.relationships.length % colors.length;

      const marriage = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_rel',
        from: parentPerson.id,
        to: newPartner.id,
        type: 'marriage',
        color: colors[colorIndex],
        lineStyle: 'default',
        startDate: '',
        endDate: '',
        isActive: true,
        abbr: state.REL_ABBREVIATIONS?.['marriage'] || '',
        notes: '',
        bubblePosition: 0.5
      };

      // Create child relationship
      const childRelationship = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_childrel',
        from: marriage.id,
        to: childPerson.id,
        type: 'child',
        color: marriage.color,
        lineStyle: 'default',
        isActive: true
      };

      // Update state with all items
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          people: [...state.people, newPartner],
          relationships: [...state.relationships, marriage, childRelationship],
          selectedPerson: childPerson,
          selectedRelationship: null,
          isConnecting: false,
          connectingFrom: null,
          connectingFromMarriage: null,
          connectingType: 'marriage',
          connectionPreview: null,
          sidePanelOpen: false
        }
      });
    };

    const createChildConnectionWithSingleAdoption = (parentPerson, childPerson) => {
      console.log('createChildConnectionWithSingleAdoption called');

      // Create adoption relationship (self-referential)
      const adoptionRelationship = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_adopt',
        from: parentPerson.id,
        to: parentPerson.id,
        type: 'adoption',
        color: '#06b6d4',
        lineStyle: 'dotted',
        isActive: true,
        notes: 'Single parent adoption',
        bubblePosition: 0.5,
        abbr: 'A'
      };

      // Create child relationship
      const childRelationship = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_childrel',
        from: adoptionRelationship.id,
        to: childPerson.id,
        type: 'child',
        color: adoptionRelationship.color,
        lineStyle: 'dashed',
        isActive: true
      };

      // Update state with all items
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          people: state.people,
          relationships: [...state.relationships, adoptionRelationship, childRelationship],
          selectedPerson: childPerson,
          selectedRelationship: null,
          isConnecting: false,
          connectingFrom: null,
          connectingFromMarriage: null,
          connectingType: 'marriage',
          connectionPreview: null,
          sidePanelOpen: false
        }
      });
    };

    const changeRelationshipTypePreservingChildren = (relationshipId, newType) => {
      console.log('=== CHANGE RELATIONSHIP TYPE - PRESERVING CHILDREN ===');
      console.log('Relationship ID:', relationshipId);
      console.log('New type:', newType);
      
      const relationship = state.relationships.find(r => r.id === relationshipId);
      if (!relationship) {
        console.error('Relationship not found:', relationshipId);
        return;
      }
      
      // Check if this relationship has children
      const childRelationships = state.relationships.filter(r => 
        r.type === 'child' && r.from === relationshipId
      );
      
      if (childRelationships.length > 0) {
        console.log(`✅ Preserving ${childRelationships.length} child relationships while changing type`);
        console.log('Children:', childRelationships.map(r => {
          const child = state.people.find(p => p.id === r.to);
          return child ? child.name : 'Unknown';
        }));
      }
      
      // Update the relationship type while preserving all children
      dispatch({
        type: 'UPDATE_RELATIONSHIP',
        payload: {
          id: relationshipId,
          updates: {
            type: newType,
            isActive: !['divorce', 'separation', 'nullity'].includes(newType)
          }
        }
      });
      
      console.log(`✅ Successfully changed relationship type to "${newType}" while preserving all children`);
    };

    const addChildToRelationship = (relationshipId) => {
  console.log('=== UPDATED ADD CHILD FUNCTION - V3 ===');
  console.log('Called with relationshipId:', relationshipId);

  const relationship = state.relationships.find(r => r.id === relationshipId);
  if (!relationship) {
    console.error('Relationship not found!', relationshipId);
    return;
  }

  // Find the parents
  const parent1 = state.people.find(p => p.id === relationship.from);
  const parent2 = state.people.find(p => p.id === relationship.to);

  if (!parent1 && !parent2) {
    console.error('No parents found for relationship!');
    return;
  }

  // Find all existing children of THIS specific relationship (siblings)
  const existingChildRelationships = state.relationships.filter(r =>
    r.type === 'child' && r.from === relationshipId
  );

  const existingSiblings = existingChildRelationships.map(rel =>
    state.people.find(p => p.id === rel.to)
  ).filter(Boolean);

  console.log('Found', existingSiblings.length, 'existing siblings');

  // Calculate base position (centered between parents)
  let baseX, baseY;

  if (parent1 && parent2) {
    baseX = (parent1.x + parent2.x) / 2;
    baseY = Math.max(parent1.y, parent2.y) + 150;
  } else if (parent1) {
    baseX = parent1.x;
    baseY = parent1.y + 150;
  } else {
    baseX = parent2.x;
    baseY = parent2.y + 150;
  }

  console.log('Base position (center between parents):', { baseX, baseY });

  // Create a copy of all people to update
  const updatedPeople = [...state.people];
  
  // NEW APPROACH: Calculate positions for ALL children including the new one
  const totalChildren = existingSiblings.length + 1;
  const SPACING = 100; // Large spacing to prevent crowding
  const totalWidth = (totalChildren - 1) * SPACING;
  const startX = snapToGridFunc(baseX - (totalWidth / 2));
  
  console.log(`Arranging ${totalChildren} children with ${SPACING}px spacing`);
  console.log(`Total width: ${totalWidth}px, Starting X: ${startX}`);
  
  // Sort existing siblings by current X position
  const sortedSiblings = [...existingSiblings].sort((a, b) => a.x - b.x);
  
  // Reposition ALL existing siblings
  sortedSiblings.forEach((sibling, index) => {
    const newX = snapToGridFunc(startX + (index * SPACING));
    const personIndex = updatedPeople.findIndex(p => p.id === sibling.id);
    
    if (personIndex !== -1) {
      const oldX = updatedPeople[personIndex].x;
      console.log(`Moving sibling ${index + 1} "${sibling.name}" from X=${oldX} to X=${newX}`);
      updatedPeople[personIndex] = {
        ...updatedPeople[personIndex],
        x: newX
      };
    }
  });
  
  // Position for the new child (at the end)
  const newChildX = snapToGridFunc(startX + (sortedSiblings.length * SPACING));
  const newChildY = snapToGridFunc(baseY);
  
  console.log(`New child will be placed at X=${newChildX}`);

  // Create the new child
  const child = {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    name: 'New Child',
    type: NodeType.PERSON,
    gender: 'unknown',
    x: newChildX,
    y: newChildY,
    age: '',
    birthDate: '',
    isDeceased: false,
    specialStatus: null,
    networkMember: false
  };

  // Create child relationship
  const childRelationship = {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_childrel',
    from: relationshipId,
    to: child.id,
    type: 'child',
    color: relationship.color || '#3b82f6',
    lineStyle: 'default',
    isActive: true
  };

  // Add the new child
  updatedPeople.push(child);

  console.log('=== FINAL SUMMARY ===');
  console.log(`Repositioned ${sortedSiblings.length} siblings`);
  console.log(`Added 1 new child at X=${newChildX}`);
  console.log(`All ${totalChildren} children are spaced ${SPACING}px apart`);

  // Update state
  dispatch({
    type: 'BATCH_UPDATE',
    payload: {
      people: updatedPeople,
      relationships: [...state.relationships, childRelationship],
      selectedPerson: child,
      selectedRelationship: null,
      isConnecting: false,
      connectingFrom: null,
      connectingFromMarriage: null,
      connectingType: null,
      connectionPreview: null,
      newPersonModalOpen: true,
      sidePanelOpen: true
    }
  });
};

    return {
      addPerson,
      updatePerson,
      deletePerson,
      setNextPersonPosition,
      setNewPersonModal,
      addRelationship,
      updateRelationship,
      deleteRelationship,
      addHousehold,
      updateHousehold,
      deleteHousehold,
      addTextBox,
      updateTextBox,
      deleteTextBox,
      setCustomAttributes,
      selectPerson,
      selectRelationship,
      selectTextBox,
      selectHousehold,
      clearSelection,
      setFocusedNode,
      exitFocusMode,
      handlePersonClick,
      startConnection,
      cancelConnection,
      startDrawingHousehold,
      addHouseholdPoint,
      finishHousehold,
      cancelDrawingHousehold,
      setContextMenu,
      setDeleteConfirmation,
      toggleSidePanel,
      setSidePanelOpen,
      setQuickAddOpen,
      setQuickEditOpen,
      setMobileMenuOpen,
      setFamilyFinderOpen,
      setZoom,
      setPan,
      setConnectionPreview,
      toggleSnapToGrid,
      toggleHighlightNetwork,
      setFileName,
      setDirty,
      loadData,
      newGenogram,
      saveToHistory,
      undo,
      redo,
      createRelationship,
      createChildRelationship,
      createChildWithUnknownParent,
      createSingleParentAdoption,
      connectExistingPersonAsChild,
      connectChildToExistingRelationship,
      handleChildConnectionOption,
      changeRelationshipTypePreservingChildren,
      addChildToRelationship
    };
  }, [
    dispatch,
    snapToGridFunc,
    state.people,
    state.relationships,
    state.highlightNetwork,
    state.REL_ABBREVIATIONS,
    state.selectedPerson,
    state.sidePanelOpen,
    state.focusedNodeId
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const networkPeople = state.people.filter((person) => person.networkMember);
    const members = networkPeople.map((person) => {
      const {
        id,
        name,
        role = '',
        networkNotes = '',
        relationshipToChild = '',
        networkRelationship = '',
        lastContactAt,
        networkLastContactAt,
        notes = ''
      } = person;

      const relationship = relationshipToChild || networkRelationship || '';

      return {
        _id: id,
        id,
        name,
        role,
        relationshipToChild: relationship,
        lastContactAt: lastContactAt || networkLastContactAt || null,
        notes: networkNotes || notes || '',
        sourceProvenance: ['genogram'],
        rawPerson: { ...person }
      };
    });

    const relationships = state.relationships.map((relationship) => ({ ...relationship }));

    const payload = {
      members,
      relationships
    };

    const serialized = JSON.stringify({
      members: members.map((member) => ({
        _id: member._id,
        name: member.name,
        role: member.role,
        relationshipToChild: member.relationshipToChild,
        lastContactAt: member.lastContactAt,
        notes: member.notes
      })),
      relationships: relationships.map((relationship) => ({
        id: relationship.id || relationship._id,
        from: relationship.from,
        to: relationship.to,
        type: relationship.type
      }))
    });

    if (serialized === lastBroadcastRef.current) return;
    lastBroadcastRef.current = serialized;

    window.__genogramNetworkState = payload;
    window.dispatchEvent(
      new CustomEvent('genogram:network-members-changed', {
        detail: payload
      })
    );
  }, [state.people, state.relationships]);

  const contextValue = {
    ...state,
    isMobile: effectiveIsMobile,
    isTouch: effectiveIsTouch,
    uiMode: userInteractionOverride, // 'touch' | 'desktop' | null
    actions, // Make sure actions are included
    state, // Include state as a property for backward compatibility
    dispatch // Include dispatch for direct access if needed
  };

  return (
    <GenogramContext.Provider value={contextValue}>
      {children}
    </GenogramContext.Provider>
  );
}

export const useGenogram = () => {
  const context = useContext(GenogramContext);
  if (!context) {
    throw new Error('useGenogram must be used within a GenogramProvider');
  }
  return context;
};